import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, LogOut, KeyRound, Eye, EyeOff, X } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import FontSizeControl from './FontSizeControl';
import { API } from '../lib/api';

export default function UserMenu({ user, onLogout }) {
  // Avatar dropdown menu: username/role header, Theme toggle, Font-size control,
  // Change Password, divider, Logout. Closes on outside click or Escape.
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [pwdCurrent,  setPwdCurrent]  = useState('');
  const [pwdNew,      setPwdNew]      = useState('');
  const [pwdConfirm,  setPwdConfirm]  = useState('');
  const [pwdShowNew,  setPwdShowNew]  = useState(false);
  const [pwdLoading,  setPwdLoading]  = useState(false);
  const [pwdError,    setPwdError]    = useState('');
  const [pwdSuccess,  setPwdSuccess]  = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Calculate fixed position from trigger so dropdown escapes overflow:hidden containers
  const toggleMenu = useCallback(() => {
    if (!open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setDropdownPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
    }
    setOpen(o => !o);
  }, [open]);

  const openChangePwd = () => {
    setOpen(false);
    setPwdCurrent(''); setPwdNew(''); setPwdConfirm('');
    setPwdError(''); setPwdSuccess(false);
    setChangePwdOpen(true);
  };

  const submitChangePwd = async (e) => {
    e.preventDefault();
    if (!pwdCurrent)       { setPwdError('Enter your current password.'); return; }
    if (pwdNew.length < 6) { setPwdError('New password must be at least 6 characters.'); return; }
    if (pwdNew !== pwdConfirm) { setPwdError('Passwords do not match.'); return; }
    if (pwdNew === pwdCurrent) { setPwdError('New password must differ from the current one.'); return; }
    setPwdLoading(true); setPwdError('');
    try {
      const res = await fetch(`${API}/auth/change-own-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user?.token}` },
        body: JSON.stringify({ currentPassword: pwdCurrent, newPassword: pwdNew, confirmPassword: pwdConfirm })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setPwdSuccess(true);
        setTimeout(() => setChangePwdOpen(false), 1800);
      } else {
        setPwdError(data.message || data.error || 'Password change failed.');
      }
    } catch (err) {
      setPwdError(`Network error: ${err?.message || 'Failed to fetch'}`);
    }
    setPwdLoading(false);
  };

  const ref2 = useRef(null); // change-password modal anchor
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      // Also exclude the portal dropdown — it renders to document.body outside ref.current
      const insideTrigger  = ref.current && ref.current.contains(e.target);
      const insideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!insideTrigger && !insideDropdown) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]); // open must be a dep — listeners only registered when menu is open

  const initials = (user?.username || 'SM').substring(0, 2).toUpperCase();

  return (
    <>
    <div ref={ref} style={{ position: 'relative' }} data-testid="user-menu">
      <button
        ref={triggerRef}
        onClick={toggleMenu}
        title={user?.username}
        data-testid="user-menu-trigger"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '4px 10px 4px 4px', borderRadius: 999,
          border: '1px solid var(--border, #e2e8f0)',
          background: open ? 'var(--surface-3, #f1f5f9)' : 'transparent',
          cursor: 'pointer', transition: 'background 150ms'
        }}
      >
        <div className="avatar" style={{ margin: 0 }}>{initials}</div>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user?.username || 'User'}
        </span>
        <ChevronDown size={12} style={{ color: 'var(--text-muted, #64748b)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          data-testid="user-menu-dropdown"
          style={{
            position: 'fixed', top: dropdownPos.top, right: dropdownPos.right,
            minWidth: 280, zIndex: 99999,
            background: 'var(--bg-surface, #fff)', color: 'var(--text-primary, #0f172a)',
            border: '1px solid var(--border, #e2e8f0)', borderRadius: 10,
            boxShadow: '0 20px 50px rgba(15,23,42,0.18)',
            backdropFilter: 'blur(8px)'
          }}
        >
          {/* Header */}
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border, #e2e8f0)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="avatar" style={{ margin: 0, width: 36, height: 36, fontSize: 12 }}>{initials}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.username || 'User'}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {user?.isSuperAdmin ? 'Super Admin' : (user?.role || 'User')}
              </div>
              {user?.email && (
                <div style={{ fontSize: 10, color: 'var(--text-muted, #64748b)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
              )}
            </div>
          </div>

          {/* Theme row */}
          <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Theme</span>
            <ThemeToggle />
          </div>

          {/* Font size row */}
          <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, borderTop: '1px solid var(--border, #e2e8f0)' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Font Size</span>
            <FontSizeControl />
          </div>

          {/* Change Password */}
          <button
            onClick={openChangePwd}
            data-testid="user-menu-change-password"
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderTop: '1px solid var(--border, #e2e8f0)',
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 11, fontWeight: 700, color: 'var(--text-primary, #0f172a)',
              textTransform: 'uppercase', letterSpacing: '0.06em', transition: 'background 150ms'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-3, #f1f5f9)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <KeyRound size={13} style={{ color: '#3b82f6' }} /> Change Password
          </button>

          {/* Logout */}
          <button
            onClick={() => { setOpen(false); onLogout(); }}
            data-testid="user-menu-logout"
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10,
              padding: '12px 14px', borderTop: '1px solid var(--border, #e2e8f0)',
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.06em',
              transition: 'background 150ms'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <LogOut size={14} /> Logout
          </button>
        </div>,
        document.body
      )}
  </div>

    {/* Change Password Modal */}
    {changePwdOpen && (
      <div
        data-testid="change-password-modal"
        style={{
          position: 'fixed', inset: 0, zIndex: 2100,
          background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
        }}
        onClick={(e) => { if (e.target === e.currentTarget) setChangePwdOpen(false); }}
      >
        <div ref={ref2} style={{
          width: '100%', maxWidth: 420,
          background: 'var(--auth-card-bg, #1e293b)',
          border: '1px solid var(--auth-card-border, #334155)',
          borderRadius: 16, padding: 28,
          boxShadow: '0 32px 64px rgba(0,0,0,0.5)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <KeyRound size={17} color="#fff" />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--auth-text, #f8fafc)' }}>Change Password</div>
                <div style={{ fontSize: 10, color: 'var(--auth-text-muted, #94a3b8)' }}>{user?.username}</div>
              </div>
            </div>
            <button onClick={() => setChangePwdOpen(false)} data-testid="change-pwd-close"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}>
              <X size={16} />
            </button>
          </div>

          {pwdSuccess ? (
            <div style={{ textAlign: 'center', padding: '18px 0', color: '#10b981', fontWeight: 800 }}>
              Password updated successfully
            </div>
          ) : (
            <form onSubmit={submitChangePwd} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label className="auth-label">Current Password</label>
                <input type="password" value={pwdCurrent} onChange={e => setPwdCurrent(e.target.value)}
                  className="auth-input" placeholder="Enter current password"
                  data-testid="change-pwd-current" autoFocus />
              </div>
              <div>
                <label className="auth-label">New Password</label>
                <div style={{ position: 'relative' }}>
                  <input type={pwdShowNew ? 'text' : 'password'} value={pwdNew}
                    onChange={e => setPwdNew(e.target.value)} className="auth-input"
                    placeholder="At least 6 characters" style={{ paddingRight: 40 }}
                    data-testid="change-pwd-new" />
                  <button type="button" onClick={() => setPwdShowNew(p => !p)}
                    className="auth-show-pass-btn" tabIndex={-1} style={{ top: '50%', transform: 'translateY(-50%)' }}>
                    {pwdShowNew ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="auth-label">Confirm New Password</label>
                <input type="password" value={pwdConfirm} onChange={e => setPwdConfirm(e.target.value)}
                  className={`auth-input ${pwdConfirm && pwdConfirm !== pwdNew ? 'error' : ''}`}
                  placeholder="Re-type new password" data-testid="change-pwd-confirm" />
              </div>
              {pwdError && (
                <div className="auth-error-banner" data-testid="change-pwd-error">{pwdError}</div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button type="button" onClick={() => setChangePwdOpen(false)}
                  className="btn btn-outline" style={{ flex: '0 0 90px', justifyContent: 'center' }}
                  data-testid="change-pwd-cancel">
                  Cancel
                </button>
                <button type="submit" disabled={pwdLoading} className="auth-submit"
                  style={{ flex: 1 }} data-testid="change-pwd-submit">
                  {pwdLoading ? 'Updating…' : <><KeyRound size={12} /> Update Password</>}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    )}
  </>
  );
}
